import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { toast } from 'react-toastify'
import { updateTask } from 'services/taskService'
import { useSeriesScope } from 'features/taskManagement/useSeriesScope'
import { listContractors } from 'services/contractorService'

const TaskContractorQuickEdit = ({ task, onSaved }) => {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const [contractors, setContractors] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  const currentId = task?.contractor?.id || task?.contractorId || null

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const width = 300
      const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12))
      setCoords({ top: Math.min(r.bottom + 6, window.innerHeight - 360), left })
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false)
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open || contractors.length) return
    setLoading(true)
    listContractors()
      .then(res => setContractors(res?.data?.data?.contractors || []))
      .catch(() => setContractors([]))
      .finally(() => setLoading(false))
  }, [open, contractors.length])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return contractors
    return contractors.filter(c => [c.name, c.company, c.trade].filter(Boolean).some(v => String(v).toLowerCase().includes(q)))
  }, [contractors, search])

  const { promptNode, requestScope } = useSeriesScope(task)

  const pick = async contractorId => {
    if ((contractorId || null) === currentId) { setOpen(false); return }
    const scope = await requestScope()
    if (scope === null) return
    setBusy(true)
    try {
      await updateTask({ id: task.id, contractorId: contractorId || null, scope })
      toast.success('Contractor updated.')
      setOpen(false)
      onSaved?.()
    } catch (e) {
      toast.error('Could not update the contractor. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (!task?.id) return null

  const currentName = task?.contractor?.name
    || (currentId ? (contractors.find(c => c.id === currentId)?.name || 'Assigned') : null)

  return (
    <>
      <Trigger
        ref={btnRef}
        type='button'
        title='Set contractor'
        $empty={!currentId}
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
      >
        <EngineeringOutlinedIcon style={{ fontSize: 14 }} />
        <TriggerText>{currentName || 'Set contractor'}</TriggerText>
      </Trigger>

      {open && coords && createPortal(
        <Pop ref={popRef} role='dialog' aria-label='Set contractor' style={{ top: coords.top, left: coords.left }} onClick={e => e.stopPropagation()}>
          <PopLabel>Set contractor</PopLabel>
          <SearchRow>
            <SearchRoundedIcon style={{ fontSize: 16, color: '#9aa0ab' }} />
            <SearchInput placeholder='Search contractors…' value={search} autoFocus onChange={e => setSearch(e.target.value)} />
          </SearchRow>
          <List>
            <RowItem type='button' $checked={!currentId} disabled={busy} onClick={() => pick(null)}>
              <Radio $on={!currentId} />
              <RowText><RowName>None</RowName></RowText>
            </RowItem>
            {filtered.map(c => (
              <RowItem key={c.id} type='button' $checked={c.id === currentId} disabled={busy} onClick={() => pick(c.id)}>
                <Radio $on={c.id === currentId} />
                <RowText>
                  <RowName>{c.name}</RowName>
                  {(c.company || c.trade) && <RowMeta>{[c.company, c.trade].filter(Boolean).join(' · ')}</RowMeta>}
                </RowText>
              </RowItem>
            ))}
            {loading && <Empty>Loading…</Empty>}
            {!loading && filtered.length === 0 && <Empty>No contractors found.</Empty>}
          </List>
        </Pop>,
        document.body
      )}
      {promptNode}
    </>
  )
}

export default TaskContractorQuickEdit

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 4px 9px;
  border-radius: 999px;
  border: 1px dashed ${({ $empty, theme }) => ($empty ? theme.colors.grey[500] : 'transparent')};
  background: ${({ $empty }) => ($empty ? '#fff' : '#f2f4f7')};
  color: ${({ $empty, theme }) => ($empty ? theme.colors.grey[800] : theme.colors.haiti)};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 150ms ease, border-color 150ms ease;
  &:hover { background: #e8f6f7; border-color: ${({ theme }) => theme.colors.secondary}; }
`
const TriggerText = styled.span`white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
const Pop = styled.div`
  position: fixed;
  z-index: 100010;
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  box-shadow: 0 18px 44px -22px rgba(12, 18, 41, 0.4);
`
const PopLabel = styled.span`font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; font-weight: 800; color: ${({ theme }) => theme.colors.grey[800]};`
const SearchRow = styled.div`display: flex; align-items: center; gap: 7px; padding: 7px 10px; border: 1px solid ${({ theme }) => theme.colors.grey[500]}; border-radius: 9px;`
const SearchInput = styled.input`border: none; outline: none; flex: 1; font: inherit; font-size: 13px; background: transparent; color: ${({ theme }) => theme.colors.haiti};`
const List = styled.div`display: flex; flex-direction: column; gap: 2px; max-height: 240px; overflow-y: auto;`
const RowItem = styled.button`
  font: inherit;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 7px 8px;
  border-radius: 9px;
  cursor: pointer;
  border: 1px solid ${({ $checked }) => ($checked ? '#bfe6ea' : 'transparent')};
  background: ${({ $checked }) => ($checked ? '#f2fbfb' : 'transparent')};
  &:hover { background: ${({ $checked }) => ($checked ? '#eaf8f9' : '#f7f8fa')}; }
  &:disabled { opacity: 0.6; cursor: default; }
`
const Radio = styled.span`
  flex: 0 0 auto;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  border: 2px solid ${({ $on }) => ($on ? '#2aa6b3' : '#cfd4dd')};
  box-shadow: ${({ $on }) => ($on ? 'inset 0 0 0 3px #fff, inset 0 0 0 7px #2aa6b3' : 'none')};
`
const RowText = styled.div`flex: 1; min-width: 0;`
const RowName = styled.div`font-size: 13px; font-weight: 600; color: #1c2029; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
const RowMeta = styled.div`font-size: 11px; color: #8a90a0;`
const Empty = styled.div`font-size: 12.5px; color: #8a90a0; padding: 10px 4px;`
